import React from "react";
import {
  DashboardOutlined as DashboardIcon,
  AssignmentTurnedInOutlined as AssignmentTurnedInIcon,
  MapOutlined as MapIcon,
  FunctionsOutlined as FunctionsIcon,
} from "@mui/icons-material";
import Sidebar, { SidebarSegment } from "./Sidebar";
import { PageType } from "../../types";

interface ProjectSidebarProps {
  projectName: string;
  currentTab: number;
  onTabChange: (tabIndex: number) => void;
  hasWarning?: (pageType: PageType) => boolean;
  /** Ob eine Seite für den aktuellen Nutzer freigegeben ist. Ohne Angabe: alles sichtbar. */
  istSichtbar?: (pageType: string) => boolean;
}

/**
 * Projekt-Navigation: vier Module, darunter die jeweiligen Seiten.
 * Die Indizes entsprechen der Tab-Zuordnung in ProjectLayout.
 *
 * Die Sidebar zeigt nur die Fachlichkeit. Einzelne Anträge stehen hier nicht; sie werden im
 * Seitenkopf gewählt, ihre Seiten Antrag, Nachweise und Kosten sind dort Tabs.
 *
 * Nachweise und Kosten stehen im Modul Genehmigung schon als eigene Seiten, sind aber noch nicht
 * wählbar: Sie werden erst projektweit, wenn Nachweis und Antrag fachlich getrennt sind
 * (`refactorings/nachweise-und-kosten-unabhaengig-vom-antrag.md`). Bis dahin erreicht man sie
 * über den Antrag.
 */
const ProjectSidebar: React.FC<ProjectSidebarProps> = ({
  currentTab,
  onTabChange,
  hasWarning,
  istSichtbar,
}) => {
  const alleSegmente: SidebarSegment[] = [
    {
      items: [
        {
          label: "Übersicht",
          icon: DashboardIcon,
          pages: [{ label: "Übersicht", index: 0, key: "project-overview" }],
        },
        {
          label: "Fläche",
          icon: MapIcon,
          pages: [
            { label: "Flächensicherung", index: 3, key: "project-area" },
            {
              label: "Anlagen",
              index: 4,
              key: "project-generation-units",
            },
            { label: "Erträge", index: 5, key: "project-yield" },
          ],
        },
        {
          label: "Genehmigung",
          icon: AssignmentTurnedInIcon,
          pages: [
            { label: "Anträge", index: 2, key: "project-permit-applications" },
            {
              label: "Nachweise",
              index: 10,
              key: "project-permit-evidences",
              disabled: true,
            },
            {
              label: "Kosten",
              index: 11,
              key: "project-permit-costs",
              disabled: true,
            },
          ],
        },
        {
          label: "Kalkulation",
          icon: FunctionsIcon,
          pages: [
            { label: "Erlöse", index: 6, key: "project-revenues" },
            { label: "Kosten", index: 7, key: "project-costs" },
            { label: "Finanzierung", index: 8, key: "project-financing" },
            {
              label: "Wirtschaftlichkeit",
              index: 9,
              key: "project-calculation",
            },
          ],
        },
      ],
    },
  ];

  // Nicht freigegebene Seiten erscheinen gar nicht erst; ein Modul ohne sichtbare Seite
  // verschwindet mit. Gefiltert wird hier, damit die Sidebar-Komponente nur fertige
  // Navigationsdaten bekommt und nichts über Berechtigungen wissen muss.
  const segments: SidebarSegment[] = istSichtbar
    ? alleSegmente
        .map((segment) => ({
          ...segment,
          items: segment.items
            .map((item) => ({
              ...item,
              pages: (item.pages ?? []).filter((page) =>
                istSichtbar(page.key),
              ),
            }))
            .filter((item) => (item.pages ?? []).length > 0),
        }))
        .filter((segment) => segment.items.length > 0)
    : alleSegmente;

  return (
    <Sidebar
      segments={segments}
      currentTab={currentTab}
      onTabChange={onTabChange}
      hasWarning={
        hasWarning ? (key: string) => hasWarning(key as PageType) : undefined
      }
    />
  );
};

export default ProjectSidebar;
